import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js/max';

export const DEFAULT_COUNTRY: CountryCode = 'US';

export const isPhoneNumberValid = (value: string): boolean =>
  !!parsePhoneNumberFromString(value)?.isValid();

export interface SplitPhoneNumber {
  country: CountryCode;
  national: string;
}

export const splitE164 = (
  value?: string | null,
  fallbackCountry: CountryCode = DEFAULT_COUNTRY
): SplitPhoneNumber => {
  if (!value) {
    return { country: fallbackCountry, national: '' };
  }

  const parsed = parsePhoneNumberFromString(value);

  if (parsed?.country) {
    return { country: parsed.country, national: parsed.nationalNumber };
  }

  const digits = value.replace(/\D/g, '');
  const callingCode = getCountryCallingCode(fallbackCountry);

  return {
    country: fallbackCountry,
    national: digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits
  };
};

export const toE164 = (country: CountryCode, nationalDigits: string): string => {
  if (!nationalDigits) {
    return '';
  }

  const parsed = parsePhoneNumberFromString(nationalDigits, country);

  return parsed?.isValid() ? parsed.number : `+${getCountryCallingCode(country)}${nationalDigits}`;
};

export const matchesCallingCode = (
  value: string | null | undefined,
  country: CountryCode
): boolean => {
  if (!value) {
    return true;
  }

  const callingCode = getCountryCallingCode(country);
  const parsed = parsePhoneNumberFromString(value);

  return parsed
    ? parsed.countryCallingCode === callingCode
    : value.startsWith(`+${callingCode}`);
};

export interface CountryOption {
  value: CountryCode;
  label: string;
  short: string;
}

const regionName = (() => {
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'region' });
    return (code: string) => names.of(code) ?? code;
  } catch {
    return (code: string) => code;
  }
})();

export const countryOptions: CountryOption[] = getCountries()
  .map((value) => {
    const callingCode = `+${getCountryCallingCode(value)}`;
    return {
      value,
      label: `${regionName(value)} (${value}) ${callingCode}`,
      short: `${value} ${callingCode}`
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label));
